type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: Record<string, unknown>;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

function isErrorLike(value: object): value is { name: string; message: string } {
  return (
    value instanceof Error ||
    (typeof DOMException !== 'undefined' && value instanceof DOMException)
  );
}

/**
 * Redact sensitive data from context objects.
 * Replaces values for keys matching sensitive patterns with '[REDACTED]'.
 * Uses word-boundary matching to avoid false positives.
 */
function redactSensitive(obj: any): any {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  // An Error keeps name and message on its prototype chain, and a DOMException
  // (what getUserMedia rejects with) does not even inherit from Error in every
  // engine — Object.entries sees neither, so the reason was logged as `{}`.
  if (isErrorLike(obj)) {
    return { name: obj.name, message: obj.message };
  }

  if (Array.isArray(obj)) {
    return obj.map(redactSensitive);
  }

  const redacted: any = {};
  for (const [key, value] of Object.entries(obj)) {
    // Match: token, password, secret, dsn, key (word boundaries)
    // Also match: sign, signing, signer, etc. but NOT signal, signalStrength
    if (/\b(token|password|secret|dsn|key)\b|sign(?!al)/i.test(key)) {
      redacted[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      redacted[key] = redactSensitive(value);
    } else {
      redacted[key] = value;
    }
  }
  return redacted;
}

/**
 * Get current environment, with lazy evaluation to avoid import-time dependency on env module.
 * Falls back to 'development' if VITE_ENVIRONMENT is not set.
 */
function getEnvironment(): string {
  return import.meta.env.VITE_ENVIRONMENT || 'development';
}

/**
 * Determine if a log should be emitted based on log level and environment.
 */
function shouldLog(level: LogLevel, environment: string): boolean {
  const currentLevel = LOG_LEVELS[level];
  let minLevel: number;

  switch (environment) {
    case 'production':
      minLevel = 2; // warn+
      break;
    case 'staging':
      minLevel = 1; // info+
      break;
    default:
      minLevel = 0; // debug+ (development, qa)
  }

  return currentLevel >= minLevel;
}

/**
 * Format log entry for output.
 * Development: human-readable with styled prefix.
 * QA/Staging/Production: JSON.
 */
function formatLogEntry(entry: LogEntry, environment: string): string {
  if (environment === 'development') {
    // Human-readable format for development
    const prefix = `[${entry.level.toUpperCase()}]`;
    const contextStr = entry.context ? ` ${JSON.stringify(entry.context)}` : '';
    return `${prefix} ${entry.message}${contextStr}`;
  }

  // JSON format for QA, staging, production
  return JSON.stringify(entry);
}

/**
 * Create a log function for a specific log level.
 */
function createLogFunction(level: LogLevel) {
  return (message: string, context?: Record<string, unknown>): void => {
    const environment = getEnvironment();

    if (!shouldLog(level, environment)) {
      return;
    }

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      context: context ? redactSensitive(context) : undefined,
    };

    const output = formatLogEntry(entry, environment);

    // Use appropriate console method
    switch (level) {
      case 'debug':
        console.debug(output);
        break;
      case 'info':
        console.info(output);
        break;
      case 'warn':
        console.warn(output);
        break;
      case 'error':
        console.error(output);
        break;
    }
  };
}

/**
 * Structured logger with log-level filtering and sensitive data redaction.
 *
 * Features:
 * - Log level filtering per environment (dev/qa: debug+, staging: info+, prod: warn+)
 * - Sensitive data redaction using word-boundary regex
 * - JSON output for non-development environments
 * - Lazy environment detection (no import-time dependency on env module)
 */
export const logger = {
  debug: createLogFunction('debug'),
  info: createLogFunction('info'),
  warn: createLogFunction('warn'),
  error: createLogFunction('error'),
};
